/**
 * SMS-flow time-of-day price-increase math. Mirrors the PHP helper
 * App\Helpers\PriceHelper (getActivePriceIncreasePercentage / getNextPriceBoundary):
 * pure clock time-of-day, "from" inclusive, "to" exclusive, intervals may wrap
 * past midnight (from > to), first matching interval wins.
 */
export default {
    methods: {
        parsePriceIntervals(intervals) {
            if (!Array.isArray(intervals)) {
                return [];
            }
            let result = [];
            for (let interval of intervals) {
                if (!interval || interval.from === undefined || interval.to === undefined) {
                    continue;
                }
                let fromMinutes = this.timeToMinutes(interval.from);
                let toMinutes = this.timeToMinutes(interval.to);
                // Skip invalid or zero-length (from === to) rows.
                if (fromMinutes === null || toMinutes === null || fromMinutes === toMinutes) {
                    continue;
                }
                result.push({
                    fromMinutes,
                    toMinutes,
                    percentage: parseFloat(interval.percentage) || 0,
                });
            }
            return result;
        },
        timeToMinutes(time) {
            if (typeof time !== 'string') {
                return null;
            }
            let m = time.match(/^(\d{1,2}):(\d{2})/);
            if (!m) {
                return null;
            }
            let hours = parseInt(m[1], 10);
            let minutes = parseInt(m[2], 10);
            if (hours > 23 || minutes > 59) {
                return null;
            }
            return hours * 60 + minutes;
        },
        isMinuteWithinInterval(nowMinutes, fromMinutes, toMinutes) {
            if (fromMinutes < toMinutes) {
                return nowMinutes >= fromMinutes && nowMinutes < toMinutes;
            }
            // Wraps past midnight (from > to).
            return nowMinutes >= fromMinutes || nowMinutes < toMinutes;
        },
        getActivePriceIncreasePercentage(intervals, nowMinutes) {
            let parsed = this.parsePriceIntervals(intervals);
            for (let interval of parsed) {
                if (this.isMinuteWithinInterval(nowMinutes, interval.fromMinutes, interval.toMinutes)) {
                    return interval.percentage;
                }
            }
            return 0;
        },
        getPriceIncreaseMultiplier(intervals, nowMinutes) {
            return 1 + this.getActivePriceIncreasePercentage(intervals, nowMinutes) / 100;
        },
        /**
         * @returns {{boundaryMinutes: number, upcomingPercentage: number, wraps: boolean}|null}
         */
        getNextPriceBoundary(intervals, nowMinutes) {
            let parsed = this.parsePriceIntervals(intervals);
            if (!parsed.length) {
                return null;
            }
            let boundaries = [];
            for (let interval of parsed) {
                boundaries.push(interval.fromMinutes);
                boundaries.push(interval.toMinutes);
            }
            boundaries = [...new Set(boundaries)].sort((a, b) => a - b);

            let nextBoundary = null;
            for (let minute of boundaries) {
                if (minute > nowMinutes) {
                    nextBoundary = minute;
                    break;
                }
            }
            let wraps = false;
            if (nextBoundary === null) {
                nextBoundary = boundaries[0];
                wraps = true;
            }

            return {
                boundaryMinutes: nextBoundary,
                upcomingPercentage: this.getActivePriceIncreasePercentage(intervals, nextBoundary),
                wraps,
            };
        },
        /**
         * Parses a "HH:mm:ss" / "HH:mm" string into seconds of day.
         */
        timeToSeconds(time) {
            if (typeof time !== 'string') {
                return 0;
            }
            let m = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
            if (!m) {
                return 0;
            }
            return parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + (m[3] ? parseInt(m[3], 10) : 0);
        },
        /**
         * Formats a positive duration in seconds as "HH:mm:ss".
         */
        formatSecondsLeft(totalSeconds) {
            totalSeconds = Math.max(0, Math.floor(totalSeconds));
            let h = Math.floor(totalSeconds / 3600);
            let m = Math.floor((totalSeconds % 3600) / 60);
            let s = totalSeconds % 60;
            let pad = (n) => (n < 10 ? '0' + n : '' + n);
            return `${pad(h)}:${pad(m)}:${pad(s)}`;
        },
    },
};
